/**
 * assets/ultem-case-catalog.js
 *
 * Shared resolver for Ultem case variants on the Ultem Lid with Metal Base
 * listing, used by the "Swapped" option in the Combined V2, Secret Menu V2 and
 * Magnetic Set builders. Attaches to `theme.ultemCaseCatalog`.
 *
 * Why it exists: that listing has 658 variants and every magnetic piece sits
 * past 250, so `product.variants` and the ordinary search payloads cannot see
 * them. The route is:
 *
 *   1. `ultemcase--<handle>`            → the size option's values and their IDs
 *   2. `byo-product-option-state`       → every color for ONE size value,
 *      with `option_values=<id>`           resolved through product_option_value
 *
 * Both hops go through `theme.productDataFetcher`; this file never calls
 * `fetch` itself (data-fetching-pattern.md sec 5 rule 2).
 *
 * Everything is exact-match and fails closed. Option values are a catalog
 * contract: if one is renamed in Admin, Swap disappears rather than resolving
 * to the wrong case. Callers should surface `entry.error` and keep going.
 *
 * Public API:
 *   configure({ handle, url, fetcher })  → catalog (call once per page)
 *   labelFor(spec)                       → exact option-1 label, or null
 *   load(spec)                           → Promise<entry>
 *   loadMany(specs)                      → Promise<entry[]>
 *   refresh(spec)                        → Promise<entry>  (re-reads stock)
 *   peek(spec)                           → entry | null    (sync, if loaded)
 *   isSellable(variant) / capacityOf(variant, reserved)
 *   colorKey(name)
 *
 * A `spec` is one of:
 *   { size: 'Weekly Pill Case' }                     a single case
 *   { role: 'left'|'right', size: 'pill' }           a magnetic side
 *   { role: 'middle', size: 'pill', count: 1|3|4 }   a magnetic middle
 *
 * An `entry` is:
 *   { ok, label, sizeValueId, colors, missing, error, variantFor(color),
 *     hasColor(color) }
 */
(function () {
  'use strict';

  var FAMILY = 'ultemcase';
  var OPTION_STATE_SECTION_ID = 'byo-product-option-state';
  var ULTEM_CASE_PRODUCT_ID = 9078369747176;

  function isUltemCaseHandle(handle) {
    return handle === 'ultem-lid-pillcases' || handle === 'ultem-lid-with-metal-base';
  }

  // Exact option-1 values for the single cases. These equal the Combined size
  // labels, so a builder can pass its own size straight through. Weekly XS is
  // absent on purpose: there is no Ultem case for it.
  var SINGLE_SIZE_LABELS = [
    'Nano Pill Case',
    'Nano Vitamin Case',
    'Mission Pill Case',
    'Mission Vitamin Case',
    'Weekly Pill Case',
    'Weekly Vitamin Case',
    'Weekly Vitamin XL Case',
    'Weekly Vitamin 2XL Case'
  ];

  // Size names inside the magnetic option values.
  var MAGNETIC_SIZE_NAMES = {
    pill: 'Pill Case',
    vitamin: 'Vitamin Case',
    vitamin_xl: 'Vitamin XL Case'
  };

  // A middle is priced per piece by the set it belongs to, and the option value
  // says which. Count 2 has no middle in ROLE_LAYOUT, so it has no label.
  var MIDDLE_SET_SUFFIX = {
    1: 'Set of 1',
    3: 'Set of 2-4',
    4: 'Set of 2-4'
  };

  var ROLE_TITLES = { left: 'Left', right: 'Right', middle: 'Middle' };

  function handleizeValue(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  var SINGLE_SIZE_BY_KEY = SINGLE_SIZE_LABELS.reduce(function (map, label) {
    map[handleizeValue(label)] = label;
    return map;
  }, {});

  // Mirrors parseCaseSize() in the cart transform: xl before vitamin, then pill.
  // Accepts 'pill', 'vitamin_xl', 'Vitamin XL Case' and the like. 2XL is
  // rejected outright: no magnetic piece is made in that size, and letting it
  // fall through to 'vitamin' would resolve the wrong case.
  function sizeCategory(value) {
    var text = String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (!text) return null;
    if (/(^|\s)\d+xl(\s|$)/.test(text)) return null;
    if (text.indexOf('vitamin') !== -1) return /(^|\s)xl(\s|$)/.test(text) ? 'vitamin_xl' : 'vitamin';
    if (text.indexOf('pill') !== -1) return 'pill';
    return null;
  }

  function normalizeRole(value) {
    var role = String(value == null ? '' : value).toLowerCase().trim();
    return ROLE_TITLES[role] ? role : '';
  }

  function labelFor(spec) {
    if (!spec) return null;

    var role = normalizeRole(spec.role);
    if (!role) {
      // A single case: the builder's own size label, matched exactly.
      return SINGLE_SIZE_BY_KEY[handleizeValue(spec.size)] || null;
    }

    var sizeName = MAGNETIC_SIZE_NAMES[sizeCategory(spec.size)];
    if (!sizeName) return null;

    if (role !== 'middle') return 'Magnetic ' + ROLE_TITLES[role] + ' - ' + sizeName;

    var suffix = MIDDLE_SET_SUFFIX[parseInt(spec.count, 10)];
    if (!suffix) return null;
    return 'Magnetic Middle - ' + sizeName + ' - 1 Case from a ' + suffix;
  }

  function isSellable(variant) {
    if (!variant) return false;
    // An absent inventory_management field means "unknown", not "untracked";
    // only a payload that carries the field can claim a variant is untracked.
    var hasManagement = Object.prototype.hasOwnProperty.call(variant, 'inventory_management');
    return variant.available === true
      || (hasManagement && !variant.inventory_management)
      || variant.inventory_policy === 'continue';
  }

  function inventoryFloor(variant) {
    var parsed = parseInt(variant && variant.inventory_quantity, 10);
    var quantity = isFinite(parsed) ? Math.max(0, parsed) : 0;
    // Shopify's sellable flag is authoritative for at least one unit, even when
    // the payload reports 0. Mirrors getStorefrontInventoryFloor in the Magnetic
    // Set picker.
    return variant && variant.available === true && quantity < 1 ? 1 : quantity;
  }

  function capacityOf(variant, reserved) {
    if (!isSellable(variant)) return 0;
    var hasManagement = Object.prototype.hasOwnProperty.call(variant, 'inventory_management');
    if ((hasManagement && !variant.inventory_management) || variant.inventory_policy === 'continue') {
      return Infinity;
    }
    var alreadyInCart = parseInt(reserved, 10);
    return Math.max(0, inventoryFloor(variant) - (isFinite(alreadyInCart) ? alreadyInCart : 0));
  }

  function makeEntry(label, extra) {
    var entry = {
      ok: false,
      label: label || null,
      sizeValueId: null,
      colors: {},
      missing: [],
      error: null
    };
    if (extra) {
      Object.keys(extra).forEach(function (key) { entry[key] = extra[key]; });
    }
    entry.variantFor = function (colorName) {
      var key = handleizeValue(colorName);
      return key && Object.prototype.hasOwnProperty.call(entry.colors, key) ? entry.colors[key] : null;
    };
    entry.hasColor = function (colorName) {
      return !!entry.variantFor(colorName);
    };
    return entry;
  }

  function failedEntry(label, error, detail) {
    return makeEntry(label, { error: error, detail: detail == null ? null : detail });
  }

  var config = { handle: '', url: '', fetcher: null };
  var entries = {}; // "<handle>|<label>" → Promise<entry>
  var resolved = {}; // same key → entry, once settled

  function getFetcher() {
    if (config.fetcher) return config.fetcher;
    return (typeof window !== 'undefined' && window.theme && window.theme.productDataFetcher) || null;
  }

  function cacheKey(label) {
    return config.handle + '|' + label;
  }

  // Step 1: the size option's values. Cached by the fetcher for the page-load.
  function loadSizeIndex() {
    var fetcher = getFetcher();
    if (!fetcher || typeof fetcher.fetch !== 'function') {
      return Promise.resolve({ error: 'fetcher_missing' });
    }
    return fetcher.fetch(FAMILY, config.handle);
  }

  function findSizeValue(payload, label) {
    var wanted = handleizeValue(label);
    var sizes = (payload && payload.sizes) || [];
    var matches = [];
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i] && handleizeValue(sizes[i].label) === wanted) matches.push(sizes[i]);
    }
    return matches.length === 1 ? matches[0] : null;
  }

  // Step 2: every color for that one size value.
  function indexOptionState(label, sizeValue, productUrl, state) {
    if (!state || state.error) {
      return failedEntry(label, 'option_state_failed', state && state.error);
    }
    var configuredHandle = handleizeValue(config.handle);
    var returnedHandle = handleizeValue(state.handle);
    if (isUltemCaseHandle(configuredHandle) && state.product_id != null && Number(state.product_id) !== ULTEM_CASE_PRODUCT_ID) {
      return failedEntry(label, 'product_mismatch', state.product_id);
    }
    if (returnedHandle !== configuredHandle) {
      // A tab can retain the old handle across the rename. Accept its alias
      // only when the option-state response proves it is the same product.
      if (!isUltemCaseHandle(configuredHandle) || !isUltemCaseHandle(returnedHandle) || Number(state.product_id) !== ULTEM_CASE_PRODUCT_ID) {
        return failedEntry(label, 'handle_mismatch', state.handle);
      }
    }
    if (handleizeValue(state.size) !== handleizeValue(label)) {
      return failedEntry(label, 'size_mismatch', state.size);
    }

    var colors = {};
    var variants = state.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      if (!variant || !variant.id) continue;
      if (handleizeValue(variant.option1) !== handleizeValue(label)) {
        return failedEntry(label, 'variant_size_mismatch', variant.option1);
      }
      var key = handleizeValue(variant.option2);
      if (!key) continue;
      // Two variants keying to the same color would make the resolved case
      // depend on payload order, so ambiguity fails closed.
      if (Object.prototype.hasOwnProperty.call(colors, key)) {
        return failedEntry(label, 'duplicate_color', variant.option2);
      }
      colors[key] = variant;
    }

    var missing = [];
    var options = state.options || [];
    for (var j = 0; j < options.length; j++) {
      var option = options[j];
      if (!option || option.product_exists) continue;
      var missingKey = handleizeValue(option.first_color);
      if (missingKey && missing.indexOf(missingKey) === -1) missing.push(missingKey);
    }

    return makeEntry(label, {
      ok: true,
      sizeValueId: sizeValue.id,
      productUrl: productUrl,
      colors: colors,
      missing: missing
    });
  }

  function loadEntry(label) {
    return loadSizeIndex().then(function (payload) {
      if (!payload || payload.error) {
        return failedEntry(label, 'size_index_failed', payload && payload.error);
      }
      var sizeValue = findSizeValue(payload, label);
      if (!sizeValue) return failedEntry(label, 'size_not_listed');

      var productUrl = payload.url || config.url;
      if (!productUrl) return failedEntry(label, 'not_configured');

      return getFetcher()
        .fetchOptionState(productUrl, OPTION_STATE_SECTION_ID, [sizeValue.id])
        .then(function (state) { return indexOptionState(label, sizeValue, productUrl, state); });
    });
  }

  function load(spec) {
    var label = labelFor(spec);
    if (!label) return Promise.resolve(failedEntry(null, 'unknown_size'));

    // Preconditions are checked before the cache, so a call made before the
    // fetcher script has run doesn't pin a failure for the rest of the page.
    if (!config.handle) return Promise.resolve(failedEntry(label, 'not_configured'));
    var fetcher = getFetcher();
    if (!fetcher || typeof fetcher.fetch !== 'function' || typeof fetcher.fetchOptionState !== 'function') {
      return Promise.resolve(failedEntry(label, 'fetcher_missing'));
    }

    var key = cacheKey(label);
    if (Object.prototype.hasOwnProperty.call(entries, key)) return entries[key];

    var promise = loadEntry(label).then(function (entry) {
      resolved[key] = entry;
      return entry;
    });
    entries[key] = promise;
    return promise;
  }

  function refresh(spec) {
    var label = labelFor(spec);
    if (!label) return Promise.resolve(failedEntry(null, 'unknown_size'));

    var key = cacheKey(label);
    var previous = resolved[key];
    var fetcher = getFetcher();
    if (previous && previous.sizeValueId && fetcher && typeof fetcher.invalidateOptionState === 'function') {
      // Invalidate the exact URL that was fetched, which came from the size
      // index rather than the configured URL.
      fetcher.invalidateOptionState(
        previous.productUrl || config.url,
        OPTION_STATE_SECTION_ID,
        [previous.sizeValueId]
      );
    }
    delete entries[key];
    delete resolved[key];
    return load(spec);
  }

  var catalog = {
    configure: function (options) {
      var handle = String((options && options.handle) || '').trim();
      var url = String((options && options.url) || '').trim();
      if (handle !== config.handle) {
        entries = {};
        resolved = {};
      }
      config.handle = handle;
      config.url = url || config.url;
      if (options && options.fetcher) config.fetcher = options.fetcher;
      return catalog;
    },

    isConfigured: function () {
      return !!config.handle && !!getFetcher();
    },

    labelFor: labelFor,
    load: load,

    loadMany: function (specs) {
      return Promise.all((specs || []).map(function (spec) { return load(spec); }));
    },

    refresh: refresh,

    peek: function (spec) {
      var label = labelFor(spec);
      if (!label) return null;
      var key = cacheKey(label);
      return Object.prototype.hasOwnProperty.call(resolved, key) ? resolved[key] : null;
    },

    isSellable: isSellable,
    capacityOf: capacityOf,
    colorKey: handleizeValue,
    sizeCategory: sizeCategory,
    SINGLE_SIZE_LABELS: SINGLE_SIZE_LABELS
  };

  if (typeof window !== 'undefined') {
    window.theme = window.theme || {};
    window.theme.ultemCaseCatalog = catalog;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = catalog;
  }
})();
